/**
 * The fixed toolbar (`061-editor.md` §2). A single thin row above the editor.
 *
 * "It is a toolbar, not a ribbon. If an item is not in the list, it does not go in the
 * toolbar — it goes in the slash menu."
 *
 * Right-aligned: the word count and the save indicator (§8).
 */
import { useEditorState, type Editor } from '@tiptap/react';
import {
  Baseline,
  Bold,
  Code,
  Code2,
  Combine,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Underline,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { currentNumberLocale, useTranslation } from '@/lib/i18n';
import { ColorMenu } from '@/features/editor/ColorMenu';
import { ImageMenu } from '@/features/editor/ImageMenu';
import { promptForLink } from '@/features/editor/link';
import type { PaletteColor } from '@/features/editor/marks';
import { SaveState, type SaveStateKind } from '@/features/editor/SaveState';
import { type UploadContext } from '@/features/editor/uploads';

interface ToolbarProps {
  editor: Editor;
  save: SaveStateKind;
  /** The page's upload context — the image button pastes through the same door (`062` §3). */
  uploads: UploadContext;
  onOpenConflict: () => void;
  /**
   * Pull the direct child pages' content into this page (`061` §2). Absent when the host
   * cannot resolve children (e.g. an editor not mounted under a notebook page); the button
   * is then hidden rather than dead.
   */
  onGatherChildren?: (() => void) | undefined;
  /** True while children are being fetched — the gather button is disabled meanwhile. */
  gathering?: boolean | undefined;
}

function Item({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      // The editor must not lose the selection when a toolbar button is pressed.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
      className={cn(
        'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
        'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
        'disabled:pointer-events-none disabled:opacity-40',
        active && 'bg-[var(--accent)] text-[var(--foreground)]',
        '[&_svg]:size-3.5',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden className="mx-1 h-4 w-px bg-[var(--border)]" />;
}

export function Toolbar({
  editor,
  save,
  uploads,
  onOpenConflict,
  onGatherChildren,
  gathering = false,
}: ToolbarProps) {
  const { t, plural } = useTranslation();

  // `useEditorState` with a selector: the toolbar re-renders only when one of THESE
  // values changes, not on every keystroke. Subscribing to the whole editor here would
  // re-render the toolbar on every character typed anywhere in the document.
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      link: e.isActive('link'),
      highlightColor: (e.getAttributes('highlight')['color'] ?? null) as PaletteColor | null,
      textColorValue: (e.getAttributes('textColor')['color'] ?? null) as PaletteColor | null,
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      task: e.isActive('taskList'),
      quote: e.isActive('blockquote'),
      codeBlock: e.isActive('codeBlock'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
      editable: e.isEditable,
      words: e.storage['characterCount']?.words?.() ?? 0,
    }),
  });

  const chain = () => editor.chain().focus();

  // Shared with the editor's `Mod+K` binding — the button and the shortcut are the same
  // gesture and must not drift apart (`features/editor/link.ts`).
  const setLink = (): void => {
    promptForLink(editor);
  };

  return (
    <div
      data-testid="editor-toolbar"
      className="flex h-8 shrink-0 items-center gap-0.5 border-b border-[var(--border)] px-2"
    >
      <Item
        label={t('editor.toolbar.undo')}
        disabled={!state.canUndo}
        onClick={() => {
          chain().undo().run();
        }}
      >
        <Undo2 />
      </Item>
      <Item
        label={t('editor.toolbar.redo')}
        disabled={!state.canRedo}
        onClick={() => {
          chain().redo().run();
        }}
      >
        <Redo2 />
      </Item>

      <Divider />

      <Item
        label={t('editor.toolbar.heading1')}
        active={state.h1}
        onClick={() => {
          chain().toggleHeading({ level: 1 }).run();
        }}
      >
        <Heading1 />
      </Item>
      <Item
        label={t('editor.toolbar.heading2')}
        active={state.h2}
        onClick={() => {
          chain().toggleHeading({ level: 2 }).run();
        }}
      >
        <Heading2 />
      </Item>
      <Item
        label={t('editor.toolbar.heading3')}
        active={state.h3}
        onClick={() => {
          chain().toggleHeading({ level: 3 }).run();
        }}
      >
        <Heading3 />
      </Item>

      <Divider />

      <Item
        label={t('editor.toolbar.bold')}
        active={state.bold}
        onClick={() => {
          chain().toggleBold().run();
        }}
      >
        <Bold />
      </Item>
      <Item
        label={t('editor.toolbar.italic')}
        active={state.italic}
        onClick={() => {
          chain().toggleItalic().run();
        }}
      >
        <Italic />
      </Item>
      <Item
        label={t('editor.toolbar.underline')}
        active={state.underline}
        onClick={() => {
          chain().toggleUnderline().run();
        }}
      >
        <Underline />
      </Item>
      <Item
        label={t('editor.toolbar.strikethrough')}
        active={state.strike}
        onClick={() => {
          chain().toggleStrike().run();
        }}
      >
        <Strikethrough />
      </Item>
      <Item
        label={t('editor.toolbar.inlineCode')}
        active={state.code}
        onClick={() => {
          chain().toggleCode().run();
        }}
      >
        <Code />
      </Item>
      <Item label={t('editor.toolbar.link')} active={state.link} onClick={setLink}>
        <LinkIcon />
      </Item>

      <Divider />

      <ColorMenu
        variant="highlight"
        label={t('editor.color.highlight')}
        icon={<Highlighter />}
        active={state.highlightColor}
        onSelect={(color) => {
          if (color === null) chain().unsetHighlight().run();
          else chain().setHighlight({ color }).run();
        }}
      />
      <ColorMenu
        variant="text"
        label={t('editor.color.textColor')}
        icon={<Baseline />}
        active={state.textColorValue}
        onSelect={(color) => {
          if (color === null) chain().unsetTextColor().run();
          else chain().setTextColor({ color }).run();
        }}
      />

      <Divider />

      <Item
        label={t('editor.toolbar.bulletList')}
        active={state.bullet}
        onClick={() => {
          chain().toggleBulletList().run();
        }}
      >
        <List />
      </Item>
      <Item
        label={t('editor.toolbar.numberedList')}
        active={state.ordered}
        onClick={() => {
          chain().toggleOrderedList().run();
        }}
      >
        <ListOrdered />
      </Item>
      <Item
        label={t('editor.toolbar.taskList')}
        active={state.task}
        onClick={() => {
          chain().toggleTaskList().run();
        }}
      >
        <ListChecks />
      </Item>
      <Item
        label={t('editor.toolbar.quote')}
        active={state.quote}
        onClick={() => {
          chain().toggleBlockquote().run();
        }}
      >
        <Quote />
      </Item>
      <Item
        label={t('editor.toolbar.codeBlock')}
        active={state.codeBlock}
        onClick={() => {
          chain().toggleCodeBlock().run();
        }}
      >
        <Code2 />
      </Item>

      <Divider />

      <Item
        label={t('editor.toolbar.divider')}
        onClick={() => {
          chain().setHorizontalRule().run();
        }}
      >
        <Minus />
      </Item>
      <Item
        label={t('editor.toolbar.table')}
        onClick={() => {
          chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
        }}
      >
        <TableIcon />
      </Item>

      {/* `061` §2: upload a file OR embed an external URL. The upload arm is the SAME path as a
          paste — `insertImages` — so there is exactly one upload choreography to get right. */}
      <ImageMenu editor={editor} uploads={uploads} />

      {/* Gather sub-pages: replace this page's body with its direct children's content, each
          under its title, separated by horizontal rules. The tree read + fetch is the host's
          job (the editor may not import from `features/notebook`, `111` §2) — so the button
          only appears when a handler was provided. */}
      {onGatherChildren !== undefined && (
        <>
          <Divider />
          <Item
            label={t('editor.gatherChildren.label')}
            disabled={gathering || !state.editable}
            onClick={onGatherChildren}
          >
            <Combine />
          </Item>
        </>
      )}

      <div className="ml-auto flex items-center gap-3 text-[var(--muted-foreground)]">
        <span className="text-xs tabular-nums">
          {plural('editor.wordCount', state.words, {
            count: state.words.toLocaleString(currentNumberLocale()),
          })}
        </span>
        <SaveState kind={save} onOpenConflict={onOpenConflict} />
      </div>
    </div>
  );
}
